import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobProgress } from '../context/JobProgressContext';
// Enterprise progress now handled at Dashboard level via JobProgressContext
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { retryJob, redetectHighlightsJob, refreshJobThumbnail } from '../api';
import { validateLlmPromptClientSide } from '../utils/promptValidation';
import { getThumbnailWithFallback, extractVodInfo, transformKickThumbnailSize } from '../utils/thumbnails';
import { extractColorsWithCache, createCustomGradient } from '../utils/colorExtractor';
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCw,
    Trash2,
    Eye,
    Download,
    MessageSquare,
    Mic,
    Wand2,
    Sparkles,
    Scissors,
    Archive,
    Play,
    Pause,
    X,
    Loader2,
    XCircle as CancelIcon
} from 'lucide-react';

// Helper function to format elapsed time
const formatElapsedTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
        return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
        return `${mins}m ${secs}s`;
    } else {
        return `${secs}s`;
    }
};

// Helper function to sanitize and improve VOD titles
const sanitizeVodTitle = (title: string | undefined | null, vodUrl: string, platform?: string): string => {
    // If title is null or undefined (sanitized by backend), use platform-specific fallback
    if (!title) {
        if (platform === 'kick') {
            return 'Kick Stream';
        } else if (platform === 'twitch') {
            return 'Twitch VOD';
        }
        return 'Stream Recording';
    }
    
    // Handle common problematic titles
    const trimmedTitle = title.trim();
    
    if (trimmedTitle === '') {
        if (platform === 'kick') {
            return 'Kick Stream';
        } else if (platform === 'twitch') {
            return 'Twitch VOD';
        }
        return 'Stream Recording';
    }
    
    // If title is very short, generic, or contains only special characters
    if (trimmedTitle.length < 3 || /^[^\w\s]*$/.test(trimmedTitle)) {
        if (platform === 'kick') {
            return 'Kick Stream';
        } else if (platform === 'twitch') {
            return 'Twitch VOD';
        }
        return 'Stream Recording';
    }
    
    // For Turkish characters or repetitive text like "cıgı cıgı"
    if (trimmedTitle.length < 10 && /^(.+?)\s*\1+$/i.test(trimmedTitle)) {
        // Extract platform info for a better title
        if (platform === 'kick') {
            return 'Kick Stream';
        } else if (platform === 'twitch') {
            return 'Twitch VOD';
        }
        return 'Stream Recording';
    }
    
    // If title is longer than 50 characters, truncate it
    if (trimmedTitle.length > 50) {
        return trimmedTitle.substring(0, 47) + '...';
    }
    
    return trimmedTitle;
};

// Modern Circular Progress Component
const ModernCircularProgress: React.FC<{
    progress: number;
    size?: number;
    strokeWidth?: number;
    className?: string;
    children?: React.ReactNode;
}> = ({ progress, size = 36, strokeWidth = 3, className = 'text-white/20', children }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const normalizedProgress = Math.min(100, Math.max(0, progress));
    const offset = circumference - (normalizedProgress / 100) * circumference;

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg className="transform -rotate-90" width={size} height={size}>
                {/* Background circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    className="text-white/10"
                />
                {/* Progress ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className={`transition-all duration-500 ease-out ${className}`}
                    strokeLinecap="round"
                />
            </svg>
            {children && (
                <div className="absolute inset-0 flex items-center justify-center">
                    {children}
                </div>
            )}
        </div>
    );
};


// Modern Job Interface
interface Job {
    id: string;
    status: string;
    vodUrl: string;
    vodTitle?: string;
    vodThumbnailUrl?: string;
    platform?: string; // Platform name (kick/twitch)
    platformVodId?: string; // Platform-specific VOD ID
    createdAt: string;
    updatedAt: string;
    queuedAt?: string; // When job was queued/created
    startedAt?: string; // When job processing actually started
    downloadProgress?: number;
    subtitleProgress?: number;
    clipsProcessedCount?: number;
    clipsTotalCount?: number;
    currentStageDetail?: string;
    displayText?: string;
    error?: string;
    redetectionCount?: number;
    // Additional progress fields from backend
    phase?: string;
    progress?: number;
    processedFragments?: number;
    totalFragments?: number;
    downloadedSize?: string;
    downloadRate?: string;
    transcriptionRate?: string;
    processingRate?: string;
    currentClipTitle?: string;
    elapsedTime?: number;
    isIndeterminate?: boolean;
    isDownloadActuallyCompleted?: boolean;
}

interface ModernJobItemProps {
    job: Job;
    onCancelJob: (jobId: string) => Promise<void>;
    onDeleteJob: (jobId: string) => void;
    onJobRetried?: (jobId: string) => void;
}

// Job Stages with Modern Design
const JOB_STAGES = [
    {
        id: 'PENDING',
        label: 'Queued',
        icon: Pause,
        color: 'from-slate-500 to-slate-600',
        bgColor: 'bg-slate-500/10',
        description: 'Waiting in queue'
    },
    {
        id: 'PROCESSING',
        label: 'Processing',
        icon: Loader2,
        color: 'from-indigo-500 to-purple-500',
        bgColor: 'bg-indigo-500/10',
        description: 'Processing started'
    },
    {
        id: 'DOWNLOADING_VOD',
        label: 'Downloading',
        icon: Download,
        color: 'from-blue-500 to-cyan-500',
        bgColor: 'bg-blue-500/10',
        description: 'Downloading video content',
        hasProgress: true,
        progressField: 'downloadProgress'
    },
    {
        id: 'FETCHING_CHAT',
        label: 'Chat Analysis',
        icon: MessageSquare,
        color: 'from-emerald-500 to-teal-500',
        bgColor: 'bg-emerald-500/10',
        description: 'Analyzing chat messages'
    },
    {
        id: 'GENERATING_SUBTITLES',
        label: 'Audio Analysis',
        icon: Mic,
        color: 'from-purple-500 to-violet-500',
        bgColor: 'bg-purple-500/10',
        description: 'Transcribing audio content',
        hasProgress: true,
        progressField: 'subtitleProgress'
    },
    {
        id: 'DETECTING_HIGHLIGHTS',
        label: 'AI Detection',
        icon: Sparkles,
        color: 'from-yellow-500 to-orange-500',
        bgColor: 'bg-yellow-500/10',
        description: 'Finding highlight moments'
    },
    {
        id: 'CLIPPING_HIGHLIGHTS',
        label: 'Creating Clips',
        icon: Scissors,
        color: 'from-pink-500 to-rose-500',
        bgColor: 'bg-pink-500/10',
        description: 'Generating video clips',
        hasProgress: true,
        progressField: 'clipsProcessedCount'
    },
    {
        id: 'SAVING_FILES',
        label: 'Finalizing',
        icon: Archive,
        color: 'from-indigo-500 to-blue-500',
        bgColor: 'bg-indigo-500/10',
        description: 'Saving processed content'
    },
    {
        id: 'COMPLETED',
        label: 'Completed',
        icon: CheckCircle,
        color: 'from-green-500 to-emerald-500',
        bgColor: 'bg-green-500/10',
        description: 'Processing complete'
    },
    {
        id: 'FAILED',
        label: 'Failed',
        icon: XCircle,
        color: 'from-red-500 to-red-600',
        bgColor: 'bg-red-500/10',
        description: 'Processing failed'
    }
];

// Glassmorphism Progress Ring Component
const GlassProgressRing: React.FC<{
    progress: number;
    size?: number;
    strokeWidth?: number;
    className?: string;
}> = ({ progress, size = 60, strokeWidth = 3, className = '' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <div className={`relative ${className}`}>
            <svg width={size} height={size} className="transform -rotate-90">
                {/* Background ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    className="text-white/10"
                />
                {/* Progress ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className="text-white transition-all duration-300 ease-out"
                    strokeLinecap="round"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-medium text-white/90">
                    {Math.round(progress)}%
                </span>
            </div>
        </div>
    );
};

// Stage Progress Component
const StageProgress: React.FC<{
    stage: any;
    isActive: boolean;
    isCompleted: boolean;
    progress?: number;
    detail?: string;
}> = ({ stage, isActive, isCompleted, progress = 0, detail }) => {
    const Icon = stage.icon;
    
    return (
        <div className={`
            relative flex items-center gap-4 p-4 rounded-xl transition-all duration-300
            ${isActive ? 'bg-white/5 border border-white/10' : 'bg-white/[0.02]'}
            ${isCompleted ? 'opacity-60' : ''}
        `}>
            {/* Stage Icon */}
            <div className={`
                relative flex items-center justify-center w-12 h-12 rounded-xl
                bg-gradient-to-br ${stage.color} backdrop-blur-sm
                ${isActive ? 'shadow-lg shadow-black/20' : ''}
            `}>
                <Icon size={20} className="text-white" />
                
                {/* Completion Checkmark */}
                {isCompleted && !isActive && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle size={12} className="text-white" />
                    </div>
                )}
            </div>

            {/* Stage Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <h4 className="font-medium text-white/90">{stage.label}</h4>
                    
                    {/* Progress Ring for Active Stages */}
                    {isActive && stage.hasProgress && (
                        <GlassProgressRing progress={progress} size={40} />
                    )}
                </div>
                
                <p className="text-sm text-white/60 mt-1">
                    {isActive && detail ? detail : stage.description}
                </p>
                
                {/* Progress Bar for Active Stages without Ring */}
                {isActive && stage.hasProgress && progress > 0 && !stage.progressField?.includes('clipsProcessedCount') && (
                    <div className="mt-3 w-full bg-white/10 rounded-full h-2 overflow-hidden">
                        <div 
                            className={`h-full bg-gradient-to-r ${stage.color} rounded-full transition-all duration-300`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export const ModernJobItem: React.FC<ModernJobItemProps> = ({
    job: initialJob,
    onCancelJob,
    onDeleteJob,
    onJobRetried
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { activeSubscription } = useSubscription();
    const { getJobProgress, refreshProgressFromServer } = useJobProgress();
    
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showRedetectModal, setShowRedetectModal] = useState(false);
    const [newPrompt, setNewPrompt] = useState('');
    const [newPromptError, setNewPromptError] = useState<string | null>(null);
    const [isRedetecting, setIsRedetecting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [additionalClipCount, setAdditionalClipCount] = useState<number>(1);
    const [deletePreviousClips, setDeletePreviousClips] = useState<boolean>(false);
    const [timeUntilVodDeletion, setTimeUntilVodDeletion] = useState<number | null>(null);
    const [isVodDeletionTimerActive, setIsVodDeletionTimerActive] = useState<boolean>(false);
    const [vodDeletionMessage, setVodDeletionMessage] = useState<string | null>(null);
    const [timeUntilRedetectExpires, setTimeUntilRedetectExpires] = useState<number | null>(null);
    const [thumbnailGradient, setThumbnailGradient] = useState<string | null>(null);
    const [customGradientStyle, setCustomGradientStyle] = useState<string | null>(null);
    const [refreshedThumbnailUrl, setRefreshedThumbnailUrl] = useState<string | null>(null);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    
    // Enhanced timer states
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [queuedElapsed, setQueuedElapsed] = useState(0);
    const [processingElapsed, setProcessingElapsed] = useState(0);
    const [totalElapsed, setTotalElapsed] = useState(0);

    // 🎯 Simple Progress Data from JobProgressContext
    const liveProgress = getJobProgress(initialJob.id);
    
    // Debug progress update flow (reduced frequency - only log significant changes)
    React.useEffect(() => {
        if (liveProgress && import.meta.env.DEV) {
            // Only log every 10% progress or status changes
            const downloadProgress = liveProgress.downloadProgress || 0;
            const subtitleProgress = liveProgress.subtitleProgress || 0;
            
            if (downloadProgress > 0 && downloadProgress % 10 === 0) {
                console.log(`[ModernJobItem] Job ${initialJob.id} download: ${downloadProgress}%`);
            }
            if (subtitleProgress > 0 && subtitleProgress % 10 === 0) {
                console.log(`[ModernJobItem] Job ${initialJob.id} subtitle: ${subtitleProgress}%`);
            }
        }
    }, [
        Math.floor((liveProgress?.downloadProgress || 0) / 10), 
        Math.floor((liveProgress?.subtitleProgress || 0) / 10),
        initialJob.id
    ]);
    
    // Merge job data with live progress
    // Priority: initialJob (from server/database) > liveProgress (cached/socket) for all data
    
    // Debug merging logic
    if (initialJob.id === 'c36055e6-1fd2-4537-b608-cbff12f554f2' || initialJob.status === 'FAILED') {
        console.log(`[ModernJobItem] DEBUG Data merge for job ${initialJob.id}:`, {
            initialJob: {
                status: initialJob.status,
                displayText: initialJob.displayText,
                downloadProgress: initialJob.downloadProgress
            },
            liveProgress: liveProgress ? {
                status: liveProgress.status,
                displayText: liveProgress.displayText,
                downloadProgress: liveProgress.downloadProgress
            } : null
        });
    }
    
    const currentJob = liveProgress 
        ? { 
            ...liveProgress,
            ...initialJob,
            // Always prioritize database status for terminal states
            status: initialJob.status,
            // For progress fields, prioritize live progress over database values
            downloadProgress: liveProgress.downloadProgress ?? initialJob.downloadProgress,
            subtitleProgress: liveProgress.subtitleProgress ?? initialJob.subtitleProgress,
            clipsProcessedCount: liveProgress.clipsProcessedCount ?? initialJob.clipsProcessedCount,
            clipsTotalCount: liveProgress.clipsTotalCount ?? initialJob.clipsTotalCount,
            error: initialJob.error,
            // For FAILED jobs, don't use live progress displayText
            displayText: initialJob.status === 'FAILED' 
                ? initialJob.displayText 
                : (liveProgress.displayText || initialJob.displayText),
            currentStageDetail: initialJob.currentStageDetail || liveProgress.currentStageDetail,
            // Include download-related fields from live progress
            processedFragments: liveProgress.processedFragments ?? initialJob.processedFragments,
            totalFragments: liveProgress.totalFragments ?? initialJob.totalFragments,
            downloadedSize: liveProgress.downloadedSize ?? initialJob.downloadedSize,
            downloadRate: liveProgress.downloadRate ?? initialJob.downloadRate,
            phase: liveProgress.phase ?? initialJob.phase,
            isIndeterminate: liveProgress.isIndeterminate ?? initialJob.isIndeterminate,
            elapsedTime: liveProgress.elapsedTime ?? initialJob.elapsedTime,
            // Add generic progress field for transcription and other stages
            progress: liveProgress.progress ?? initialJob.progress
          }
        : initialJob;
    // Debug inconsistent status display (temporary)
    if (currentJob.displayText?.includes('Job completed') && currentJob.status !== 'COMPLETED') {
        console.warn(`[ModernJobItem] Status inconsistency detected for job ${currentJob.id}:`, {
            initialStatus: initialJob.status,
            liveProgressStatus: liveProgress?.status,
            finalStatus: currentJob.status,
            displayText: currentJob.displayText,
            liveProgress: liveProgress
        });
    }

    // Use reliable thumbnail with fallback, prefer refreshed URL if available
    const effectiveThumbnailUrl = refreshedThumbnailUrl || currentJob.vodThumbnailUrl;
    
    // For Kick VODs, transform to smaller size; for others use fallback logic
    const thumbnailInfo = React.useMemo(() => {
        // Use platform field directly if available, otherwise fall back to URL parsing
        const platform = currentJob.platform || (currentJob.vodUrl ? extractVodInfo(currentJob.vodUrl).platform : 'unknown');
        
        if (effectiveThumbnailUrl && platform === 'kick') {
            // If it's a cached URL, use it directly
            if (effectiveThumbnailUrl.includes('/api/thumbnails/cached/')) {
                return {
                    thumbnailUrl: effectiveThumbnailUrl,
                    isUsingFallback: false,
                    fallbackUrl: '/kick-default-thumbnail.svg'
                };
            }
            
            const transformedUrl = transformKickThumbnailSize(effectiveThumbnailUrl, 320);
            // Use transformed Kick thumbnail (smaller size)
            return {
                thumbnailUrl: transformedUrl,
                isUsingFallback: false,
                fallbackUrl: '/kick-default-thumbnail.svg'
            };
        }
        // For other platforms, use existing fallback logic
        return getThumbnailWithFallback(currentJob.vodUrl, effectiveThumbnailUrl);
    }, [effectiveThumbnailUrl, currentJob.vodUrl, currentJob.platform]);
    
    // Note: Removed Kick thumbnail refresh logic since new Kick CDN URLs are permanent

    // Listen for thumbnail refresh events
    useEffect(() => {
        const handleThumbnailRefresh = (event: CustomEvent) => {
            const { jobId, vodThumbnailUrl } = event.detail;
            if (jobId === currentJob.id) {
                console.log('[ModernJobItem] Received thumbnail refresh event for job:', jobId);
                setRefreshedThumbnailUrl(vodThumbnailUrl);
            }
        };

        window.addEventListener('jobThumbnailRefreshed', handleThumbnailRefresh as EventListener);
        
        return () => {
            window.removeEventListener('jobThumbnailRefreshed', handleThumbnailRefresh as EventListener);
        };
    }, [currentJob.id]);

    // Extract colors from thumbnail for completed jobs
    useEffect(() => {
        if (currentJob.status === 'COMPLETED' && thumbnailInfo.thumbnailUrl) {
            console.log('[ModernJobItem] Extracting colors from thumbnail:', {
                url: thumbnailInfo.thumbnailUrl,
                platform: currentJob.platform || (currentJob.vodUrl ? extractVodInfo(currentJob.vodUrl).platform : 'unknown')
            });
            
            extractColorsWithCache(thumbnailInfo.thumbnailUrl)
                .then(colors => {
                    console.log('[ModernJobItem] Successfully extracted colors:', colors);
                    setThumbnailGradient(`from-[${colors.primary.hex}] to-[${colors.secondary.hex}]`);
                    setCustomGradientStyle(createCustomGradient(colors));
                })
                .catch(error => {
                    console.warn('[ModernJobItem] Failed to extract colors from thumbnail:', error);
                    // Fallback to default green gradient
                    setThumbnailGradient('from-green-500 to-emerald-500');
                    setCustomGradientStyle(null);
                });
        } else {
            setThumbnailGradient(null);
            setCustomGradientStyle(null);
        }
    }, [currentJob.status, thumbnailInfo.thumbnailUrl, refreshedThumbnailUrl]);

    // Enhanced timer calculation effect
    useEffect(() => {
        let timerId: NodeJS.Timeout | undefined;
        
        const updateTimers = () => {
            // For completed/failed/cancelled jobs, use updatedAt as end time
            const now = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(currentJob.status) && currentJob.updatedAt
                ? new Date(currentJob.updatedAt).getTime()
                : Date.now();
            setCurrentTime(now);
            
            // Calculate queued elapsed time (from queuedAt or createdAt to now or startedAt)
            const queuedTime = currentJob.queuedAt ? new Date(currentJob.queuedAt).getTime() : new Date(currentJob.createdAt).getTime();
            const processingStartTime = currentJob.startedAt ? new Date(currentJob.startedAt).getTime() : null;
            
            if (processingStartTime) {
                // Job has started processing
                const queuedDuration = processingStartTime - queuedTime;
                setQueuedElapsed(Math.max(0, queuedDuration / 1000));
                
                // Calculate processing elapsed time
                const processingDuration = now - processingStartTime;
                setProcessingElapsed(Math.max(0, processingDuration / 1000));
                
                // Total elapsed time
                setTotalElapsed(Math.max(0, (now - queuedTime) / 1000));
            } else {
                // Job is still queued
                const queuedDuration = now - queuedTime;
                setQueuedElapsed(Math.max(0, queuedDuration / 1000));
                setProcessingElapsed(0);
                setTotalElapsed(Math.max(0, queuedDuration / 1000));
            }
        };
        
        // Only run timers for active jobs
        if (!['COMPLETED', 'FAILED', 'CANCELLED'].includes(currentJob.status)) {
            updateTimers();
            timerId = setInterval(updateTimers, 1000);
        } else {
            // For completed jobs, calculate final times once
            updateTimers();
        }
        
        return () => {
            if (timerId) clearInterval(timerId);
        };
    }, [currentJob.status, currentJob.queuedAt, currentJob.startedAt, currentJob.createdAt, currentJob.updatedAt]);

    // VOD deletion timer effect
    useEffect(() => {
        let timerId: NodeJS.Timeout | undefined;
        if (currentJob.status === 'COMPLETED' && currentJob.updatedAt) {
            const jobCompletedTime = new Date(currentJob.updatedAt).getTime();
            const oneHourInMs = 60 * 60 * 1000;
            const deletionTime = jobCompletedTime + oneHourInMs;
            const updateTimer = () => {
                const now = Date.now();
                const timeLeft = Math.max(0, deletionTime - now);
                setTimeUntilVodDeletion(Math.ceil(timeLeft / 1000));
                if (timeLeft <= 0) {
                    setIsVodDeletionTimerActive(false);
                    setVodDeletionMessage('VOD has been deleted. Redetection is no longer possible.');
                    if (timerId) clearInterval(timerId);
                } else {
                    setIsVodDeletionTimerActive(true);
                }
            };
            updateTimer();
            timerId = setInterval(updateTimer, 1000);
        } else {
            setIsVodDeletionTimerActive(false);
            setTimeUntilVodDeletion(null);
            setVodDeletionMessage(null);
        }
        return () => {
            if (timerId) clearInterval(timerId);
        };
    }, [currentJob.status, currentJob.updatedAt]);

    // Redetect button timer effect (10 minutes)
    useEffect(() => {
        let timerId: NodeJS.Timeout | undefined;
        if (currentJob.status === 'COMPLETED' && currentJob.updatedAt) {
            const jobCompletedTime = new Date(currentJob.updatedAt).getTime();
            const tenMinutesInMs = 10 * 60 * 1000;
            const expirationTime = jobCompletedTime + tenMinutesInMs;
            const updateTimer = () => {
                const now = Date.now();
                const timeLeft = Math.max(0, expirationTime - now);
                setTimeUntilRedetectExpires(Math.ceil(timeLeft / 1000));
                if (timeLeft <= 0 && timerId) {
                    clearInterval(timerId);
                }
            };
            updateTimer();
            timerId = setInterval(updateTimer, 1000);
        } else {
            setTimeUntilRedetectExpires(null);
        }
        return () => {
            if (timerId) clearInterval(timerId);
        };
    }, [currentJob.status, currentJob.updatedAt]);

    // Progress updates now handled centrally via JobProgressContext + Enterprise SSE

    // Get current stage and progress
    const currentStage = JOB_STAGES.find(stage => stage.id === currentJob.status) || JOB_STAGES[0];
    const currentStageIndex = JOB_STAGES.findIndex(stage => stage.id === currentJob.status);
    
    // Use thumbnail gradient for completed jobs, otherwise use default stage color
    const effectiveStageColor = currentJob.status === 'COMPLETED' && thumbnailGradient 
        ? thumbnailGradient 
        : currentStage.color;

    // Calculate stage progress with enhanced accuracy
    const getStageProgress = (stage: any) => {
        if (!stage.hasProgress) return 0;
        
        switch (stage.progressField) {
            case 'downloadProgress':
                // Priority: downloadProgress > fragment-based calculation > generic progress
                if (currentJob.downloadProgress !== undefined && currentJob.downloadProgress > 0) {
                    return Math.min(100, currentJob.downloadProgress);
                }
                if (currentJob.processedFragments !== undefined && currentJob.totalFragments && currentJob.totalFragments > 0) {
                    const fragmentProgress = (currentJob.processedFragments / currentJob.totalFragments) * 100;
                    console.log(`[ModernJobItem] Fragment progress for job ${currentJob.id}: ${fragmentProgress.toFixed(2)}% (${currentJob.processedFragments}/${currentJob.totalFragments})`);
                    return Math.min(100, fragmentProgress);
                }
                // Fallback to downloadProgress or generic progress or 0
                const downloadFallback = currentJob.downloadProgress || currentJob.progress || 0;
                // Commented out to reduce log spam - this is expected behavior for non-download stages
                // console.log(`[ModernJobItem] Using fallback progress for job ${currentJob.id}: ${downloadFallback}% (downloadProgress: ${currentJob.downloadProgress}, progress: ${currentJob.progress})`);
                return Math.min(100, downloadFallback);
                
            case 'subtitleProgress':
                // Priority: subtitleProgress > generic progress > 0
                if (currentJob.subtitleProgress !== undefined && currentJob.subtitleProgress > 0) {
                    // Removed console.log to prevent browser performance issues
                    return Math.min(100, currentJob.subtitleProgress);
                }
                // Fallback to generic progress if available
                const subtitleFallback = currentJob.progress || 0;
                // Removed console.log to prevent browser performance issues
                return Math.min(100, subtitleFallback);
                
            case 'clipsProcessedCount':
                if (currentJob.clipsTotalCount && currentJob.clipsTotalCount > 0 && currentJob.clipsProcessedCount !== undefined) {
                    const clipProgress = (currentJob.clipsProcessedCount / currentJob.clipsTotalCount) * 100;
                    return Math.min(100, clipProgress);
                }
                return Math.min(100, currentJob.progress || 0);
                
            default:
                return Math.min(100, currentJob.progress || 0);
        }
    };

    // Helper function to determine stage status - fixed to prevent all stages showing as active
    const getStageStatus = (stageId: string): 'pending' | 'active' | 'completed' | 'failed' => {
        const stageOrder = ['PENDING', 'PROCESSING', 'DOWNLOADING_VOD', 'FETCHING_CHAT', 'GENERATING_SUBTITLES', 'DETECTING_HIGHLIGHTS', 'CLIPPING_HIGHLIGHTS', 'SAVING_FILES', 'COMPLETED'];
        
        // Use actual job status for stage calculations
        const currentStageIndex = stageOrder.indexOf(currentJob.status);
        const targetStageIndex = stageOrder.indexOf(stageId);
        
        // If stage not found in order, consider it pending
        if (currentStageIndex === -1 || targetStageIndex === -1) {
            return 'pending';
        }
        
        if (currentJob.status === 'COMPLETED') {
            // For completed jobs, all processing stages should show as completed
            if (stageId === 'COMPLETED') {
                return 'completed'; // Show completed stage as completed, not active
            } else if (targetStageIndex < stageOrder.indexOf('COMPLETED')) {
                return 'completed';
            } else {
                return 'pending';
            }
        } else if (currentJob.status === 'FAILED') {
            if (targetStageIndex <= currentStageIndex) {
                return stageId === currentJob.status ? 'failed' : 'completed';
            } else {
                return 'pending';
            }
        } else if (currentJob.status === 'CANCELLED') {
            return targetStageIndex <= currentStageIndex ? 'completed' : 'pending';
        } else if (targetStageIndex < currentStageIndex) {
            return 'completed';
        } else if (targetStageIndex === currentStageIndex) {
            return 'active';
        } else {
            return 'pending';
        }
    };

    // Determine if job can be cancelled or deleted
    const canCancel = !['COMPLETED', 'FAILED', 'CANCELLED'].includes(currentJob.status);
    const canDelete = true; // Allow deletion in all states, backend will handle validation
    
    // Debug: Log current job status to understand why cancel button shows
    if (currentJob.id === '4608fce8-cb5f-4bb1-996b-9c8a05a7119c') {
        console.log(`[ModernJobItem] Job ${currentJob.id} - Status: ${currentJob.status}, canCancel: ${canCancel}, initialStatus: ${initialJob.status}, liveProgress: ${JSON.stringify(liveProgress)}`);
    }
    
    // Force refresh job from server if status seems stale
    useEffect(() => {
        if (currentJob.status === 'PROCESSING' && liveProgress === null) {
            console.log(`[ModernJobItem] Job ${currentJob.id} may have stale status, refreshing from server`);
            refreshProgressFromServer(currentJob.id);
        }
    }, [currentJob.id, currentJob.status, liveProgress, refreshProgressFromServer]);


    const handleRetryJob = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!currentJob || !currentJob.id) return;
        setIsRetrying(true);
        try {
            await retryJob(currentJob.id);
            if (onJobRetried) {
                onJobRetried(currentJob.id);
            }
        } catch (error) {
            console.error('Failed to retry job:', error);
        } finally {
            setIsRetrying(false);
        }
    };

    const handleCancelJob = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!currentJob || !currentJob.id) return;
        
        setIsCancelling(true);
        setCancelError(null);
        
        try {
            await onCancelJob(currentJob.id);
        } catch (error: any) {
            let errorMessage = error.response?.data?.message || 'Failed to cancel job. Please try again.';
            
            // Make the error message more user-friendly for common cases
            if (errorMessage.includes('status COMPLETED and cannot be cancelled')) {
                errorMessage = 'This job has already completed and cannot be cancelled.';
            } else if (errorMessage.includes('status FAILED and cannot be cancelled')) {
                errorMessage = 'This job has failed and cannot be cancelled.';
            }
            
            setCancelError(errorMessage);
            console.error(`Error cancelling job ${currentJob.id}:`, error);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRedetectSubmit = async () => {
        if (!currentJob || !currentJob.id || !newPrompt.trim()) {
            return;
        }
        setIsRedetecting(true);
        try {
            await redetectHighlightsJob(currentJob.id, newPrompt, additionalClipCount, deletePreviousClips);
            setShowRedetectModal(false);
            setNewPrompt('');
            setAdditionalClipCount(1);
            setDeletePreviousClips(false);
            if (onJobRetried) {
                onJobRetried(currentJob.id);
            }
        } catch (error) {
            console.error('Failed to redetect highlights:', error);
        } finally {
            setIsRedetecting(false);
        }
    };

    return (
        <div className="relative group">
            {/* Main Card - Glassmorphism */}
            <div className={`
                relative p-6 rounded-2xl transition-all duration-300
                bg-white/[0.02] backdrop-blur-xl border border-white/5
                hover:bg-white/[0.04] hover:border-white/10
            `}>
                {/* Gradient Overlay */}
                <div 
                    className={`
                        absolute inset-0 rounded-2xl opacity-50 transition-opacity duration-300
                        ${!customGradientStyle ? `bg-gradient-to-br ${effectiveStageColor}` : ''}
                        ${currentJob.status === 'COMPLETED' ? 'opacity-20' : 'opacity-5'}
                    `}
                    style={customGradientStyle ? { 
                        background: customGradientStyle,
                        backgroundSize: '100% 100%'
                    } : undefined}
                />

                {/* Content */}
                <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* VOD Thumbnail */}
                            <div className="flex-shrink-0">
                                <img
                                    src={thumbnailInfo.thumbnailUrl}
                                    alt="VOD thumbnail"
                                    className="w-32 h-24 object-cover rounded-lg border border-white/10"
                                    onError={(e) => {
                                        // If thumbnail fails to load, use fallback
                                        if (e.currentTarget.src !== thumbnailInfo.fallbackUrl) {
                                            e.currentTarget.src = thumbnailInfo.fallbackUrl;
                                        }
                                    }}
                                />
                                {thumbnailInfo.isUsingFallback && (
                                    <div className="absolute top-0 right-0 w-3 h-3 bg-yellow-500/20 border border-yellow-500/50 rounded-full" 
                                         title="Using fallback thumbnail" />
                                )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                {/* VOD Title with Platform Badge and Elapsed Time */}
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-lg font-semibold text-white/90 truncate flex-1">
                                        {sanitizeVodTitle(currentJob.vodTitle, currentJob.vodUrl, currentJob.platform)}
                                    </h3>
                                    {/* Status Badge for CANCELLED/FAILED */}
                                    {(currentJob.status === 'CANCELLED' || currentJob.status === 'FAILED') && (
                                        <div className={`
                                            px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm
                                            ${currentJob.status === 'CANCELLED'
                                                ? 'bg-orange-900/60 text-orange-300 border border-orange-400/40'
                                                : 'bg-red-900/60 text-red-300 border border-red-400/40'
                                            }
                                        `}>
                                            {currentJob.status}
                                        </div>
                                    )}
                                    
                                    {/* Platform Badge */}
                                    {(currentJob.platform || currentJob.vodUrl) && (
                                        <div className={`
                                            px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm
                                            ${(currentJob.platform || (currentJob.vodUrl ? extractVodInfo(currentJob.vodUrl).platform : 'unknown')) === 'kick'
                                                ? 'bg-green-900/60 text-green-300 border border-green-400/40'
                                                : 'bg-purple-900/60 text-purple-300 border border-purple-400/40'
                                            }
                                        `}>
                                            {(currentJob.platform || (currentJob.vodUrl ? extractVodInfo(currentJob.vodUrl).platform : 'unknown')).toUpperCase()}
                                        </div>
                                    )}
                                    
                                    {/* Elapsed Time Display */}
                                    {totalElapsed > 0 && !['COMPLETED', 'FAILED', 'CANCELLED'].includes(currentJob.status) && (
                                        <div className="px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm bg-blue-900/60 text-blue-300 border border-blue-400/40">
                                            {formatElapsedTime(totalElapsed)}
                                        </div>
                                    )}
                                </div>
                            
                            {/* Current Stage */}
                            <div className="flex items-center gap-2">
                                <currentStage.icon size={16} className="text-white/60" />
                                <span className="text-sm text-white/60">
                                    {currentStage.label}
                                    {currentJob.phase && ` - ${currentJob.phase}`}
                                </span>
                                {currentStage.hasProgress && !currentJob.isIndeterminate && (
                                    <span className="text-xs text-white/40">
                                        • {getStageProgress(currentStage).toFixed(2)}%
                                    </span>
                                )}
                            </div>
                            
                            {/* Additional Progress Details - Only show if different from stage label */}
                            {currentJob.displayText && 
                             !currentJob.displayText.toLowerCase().includes(currentStage.label.toLowerCase()) &&
                             // Don't show transcription info during clipping stage or when completed
                             !(['CLIPPING_HIGHLIGHTS', 'COMPLETED'].includes(currentJob.status) && 
                               (currentJob.displayText.includes('segments processed') || 
                                currentJob.displayText.includes('Transcription:'))) && (
                                <p className="text-xs text-white/50 mt-1">{currentJob.displayText}</p>
                            )}
                            
                            {/* Show completion message with clip count */}
                            {currentJob.status === 'COMPLETED' && currentJob.clipsTotalCount && currentJob.clipsTotalCount > 0 && (
                                <p className="text-xs text-emerald-400 mt-1 font-medium">
                                    ✓ Job completed! {currentJob.clipsTotalCount} clip{currentJob.clipsTotalCount > 1 ? 's' : ''} created successfully
                                </p>
                            )}
                            
                            {/* Error Message Display */}
                            {currentJob.status === 'FAILED' && currentJob.error && (
                                <div className="mt-2 p-2 rounded-lg bg-red-900/30 border border-red-400/30">
                                    <div className="flex items-start gap-2">
                                        <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-xs font-medium text-red-300">Error Details:</p>
                                            <p className="text-xs text-red-200 mt-1 break-words">{currentJob.error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {currentJob.downloadRate && currentJob.downloadedSize && 
                             currentJob.downloadedSize !== '0.0' && currentJob.downloadRate !== '0.0' && (
                                <p className="text-xs text-white/40 mt-1">
                                    {currentJob.downloadedSize} • {currentJob.downloadRate}
                                </p>
                            )}
                            </div>
                        </div>

                        {/* Status Indicator */}
                        <div className="relative flex flex-col items-end gap-2">
                            <div 
                                className={`
                                    flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-sm
                                    ${!customGradientStyle ? `bg-gradient-to-br ${effectiveStageColor}` : ''}
                                `}
                                style={customGradientStyle ? { 
                                    background: customGradientStyle 
                                } : undefined}
                            >
                                <currentStage.icon size={18} className="text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Progress Overview */}
                    {canCancel && currentStage.hasProgress && !currentJob.isIndeterminate && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between text-xs text-white/60 mb-2">
                                <span>Progress</span>
                                <span className="font-mono font-semibold">{getStageProgress(currentStage).toFixed(2)}%</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden shadow-inner">
                                <div 
                                    className={`h-full rounded-full transition-all duration-500 ease-out shadow-sm ${!customGradientStyle ? `bg-gradient-to-r ${effectiveStageColor}` : ''}`}
                                    style={{ 
                                        width: `${Math.min(getStageProgress(currentStage), 100)}%`,
                                        ...(customGradientStyle ? { background: customGradientStyle } : {})
                                    }}
                                />
                            </div>
                            
                            {/* Friendly message when download is at 95%+ */}
                            {currentJob.status === 'DOWNLOADING_VOD' && getStageProgress(currentStage) >= 95 && getStageProgress(currentStage) < 100 && (
                                <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                    <p className="text-xs text-blue-300 flex items-center gap-1.5">
                                        <Loader2 size={12} className="animate-spin" />
                                        Finalizing download - Merging video fragments and saving to storage...
                                    </p>
                                </div>
                            )}
                            
                            {/* Friendly message when transcription is at 95%+ */}
                            {currentJob.status === 'GENERATING_SUBTITLES' && getStageProgress(currentStage) >= 95 && getStageProgress(currentStage) < 100 && (
                                <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                                    <p className="text-xs text-purple-300 flex items-center gap-1.5">
                                        <Loader2 size={12} className="animate-spin" />
                                        Finalizing transcription - Processing final segments and generating subtitle files...
                                    </p>
                                </div>
                            )}
                            
                            {/* Enhanced Progress Details */}
                            <div className="mt-3 space-y-1">
                                {/* Download Progress with Speed */}
                                {currentJob.downloadedSize && currentJob.downloadedSize !== '0.0' && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-white/40">Downloaded</span>
                                        <span className="font-mono text-white/60">
                                            {currentJob.downloadedSize}MB
                                            {currentJob.downloadRate && currentJob.downloadRate !== '0.0' && ` @ ${currentJob.downloadRate} MB/s`}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Enhanced Fragment Info - Only show for DOWNLOADING_VOD stage */}
                                {currentJob.status === 'DOWNLOADING_VOD' && currentJob.processedFragments && currentJob.totalFragments && (
                                    <div className="flex items-center justify-between text-xs opacity-60">
                                        <span className="text-white/30">Fragments</span>
                                        <span className="font-mono text-white/40 text-xs">
                                            {currentJob.processedFragments}/{currentJob.totalFragments}
                                            {/* Show decimal precision progress */}
                                            <span className="text-white/30 ml-1">
                                                ({((currentJob.processedFragments / currentJob.totalFragments) * 100).toFixed(1)}%)
                                            </span>
                                        </span>
                                    </div>
                                )}
                                
                                {/* Transcription Progress - Only show for GENERATING_SUBTITLES stage */}
                                {currentJob.status === 'GENERATING_SUBTITLES' && currentJob.transcriptionRate && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-white/40">Transcription</span>
                                        <span className="font-mono text-white/60">
                                            {currentJob.transcriptionRate}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Clip Processing */}
                                {currentJob.clipsProcessedCount !== undefined && currentJob.clipsTotalCount && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-white/40">Clips</span>
                                        <span className="font-mono text-white/60">
                                            {currentJob.clipsProcessedCount}/{currentJob.clipsTotalCount}
                                            {currentJob.processingRate && ` • ${currentJob.processingRate}`}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Current Clip Title */}
                                {currentJob.currentClipTitle && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-white/40">Current</span>
                                        <span className="font-mono text-white/60 truncate max-w-32">
                                            {currentJob.currentClipTitle}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Elapsed Time */}
                                {currentJob.elapsedTime && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-white/40">Elapsed</span>
                                        <span className="font-mono text-white/60">
                                            {formatElapsedTime(currentJob.elapsedTime)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Indeterminate Progress */}
                    {canCancel && currentJob.isIndeterminate && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between text-xs text-white/60 mb-2">
                                <span>Progress</span>
                                <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse" />
                                    <span>Processing...</span>
                                </span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden shadow-inner">
                                <div 
                                    className={`h-full rounded-full relative overflow-hidden ${!customGradientStyle ? `bg-gradient-to-r ${effectiveStageColor}` : ''}`}
                                    style={customGradientStyle ? { background: customGradientStyle } : undefined}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                                </div>
                            </div>
                            
                            {/* Show available details even in indeterminate mode */}
                            {(currentJob.displayText || currentJob.elapsedTime) && (
                                <div className="mt-3 space-y-1">
                                    {currentJob.displayText && (
                                        <p className="text-xs text-white/50">{currentJob.displayText}</p>
                                    )}
                                    {currentJob.elapsedTime && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-white/40">Elapsed</span>
                                            <span className="font-mono text-white/60">
                                                {formatElapsedTime(currentJob.elapsedTime)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Stage Progress Circles (Right Side) */}
                    {(canCancel || currentJob.status === 'COMPLETED') && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between text-xs text-white/60 mb-3">
                                <span>Stages</span>
                                <span className="text-white/40">Step by step</span>
                            </div>
                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3 items-start">
                                {JOB_STAGES.filter(stage => {
                                    // Hide terminal states
                                    if (['FAILED', 'COMPLETED'].includes(stage.id)) return false;
                                    
                                    // Platform-specific filtering: Hide chat analysis for Kick
                                    const platform = currentJob.platform || (currentJob.vodUrl ? extractVodInfo(currentJob.vodUrl).platform : 'unknown');
                                    if (stage.id === 'FETCHING_CHAT' && platform === 'kick') {
                                        return false;
                                    }
                                    
                                    return true;
                                }).map((stage) => {
                                    const stageStatus = getStageStatus(stage.id);
                                    const isCompleted = stageStatus === 'completed';
                                    const isActive = stageStatus === 'active';
                                    const isFailed = stageStatus === 'failed';
                                    
                                    // Debug failing stages for non-failed jobs
                                    if (isFailed && currentJob.status !== 'FAILED') {
                                        console.warn(`[ModernJobItem] Stage ${stage.id} showing as failed but job status is ${currentJob.status} for job ${currentJob.id}`);
                                    }
                                    
                                    let stageProgress = 0;
                                    let iconColor = 'text-white/30';
                                    let circleColor = 'text-white/20';
                                    
                                    if (isCompleted) {
                                        stageProgress = 100;
                                        iconColor = 'text-emerald-400';
                                        circleColor = 'text-emerald-500';
                                    } else if (isActive) {
                                        stageProgress = stage.hasProgress ? getStageProgress(stage) : 50;
                                        iconColor = 'text-white';
                                        circleColor = stage.color.includes('blue') ? 'text-blue-400' : 
                                                    stage.color.includes('emerald') ? 'text-emerald-400' :
                                                    stage.color.includes('purple') ? 'text-purple-400' :
                                                    stage.color.includes('yellow') ? 'text-yellow-400' : 'text-cyan-400';
                                    } else if (isFailed) {
                                        stageProgress = 100;
                                        iconColor = 'text-red-400';
                                        circleColor = 'text-red-500';
                                    }
                                    
                                    return (
                                        <div key={stage.id} className="flex flex-col items-center text-center h-20">
                                            <div className="flex items-center justify-center mb-2">
                                                <ModernCircularProgress 
                                                    progress={stageProgress} 
                                                    size={40} 
                                                    strokeWidth={3}
                                                    className={circleColor}
                                                >
                                                    <stage.icon size={16} className={iconColor} />
                                                </ModernCircularProgress>
                                            </div>
                                            <p className={`text-xs leading-tight transition-colors text-center max-w-full break-words ${
                                                isCompleted ? 'text-emerald-400 font-medium' :
                                                isActive ? 'text-white font-medium' :
                                                isFailed ? 'text-red-400' : 'text-white/40'
                                            }`}>
                                                {stage.label}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    
                    {/* Time Information for completed/failed jobs */}
                    {!canCancel && (
                        <div className="flex items-center gap-4 text-xs text-white/40">
                            <span>Created: {new Date(currentJob.createdAt).toLocaleString()}</span>
                            {totalElapsed > 0 && (
                                <span>Duration: {formatElapsedTime(totalElapsed)}</span>
                            )}
                        </div>
                    )}

                    {/* Action Buttons - Inside the box, compact */}
                    <div className="mt-4 flex items-center gap-2">
                {canCancel && (
                    <button
                        onClick={handleCancelJob}
                        disabled={isCancelling}
                        className={`
                            flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200
                            bg-red-500/20 border border-red-500/30 text-red-300
                            hover:bg-red-500/30 hover:border-red-500/50 min-w-[70px]
                            ${isCancelling ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        {isCancelling ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <CancelIcon size={12} />
                        )}
                        {isCancelling ? 'Cancelling...' : 'Cancel'}
                    </button>
                )}

                {currentJob.status === 'FAILED' && onJobRetried && (
                    <button
                        onClick={handleRetryJob}
                        disabled={isRetrying}
                        className={`
                            flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200
                            bg-blue-500/20 border border-blue-500/30 text-blue-300
                            hover:bg-blue-500/30 hover:border-blue-500/50
                            disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]
                        `}
                    >
                        {isRetrying ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <RefreshCw size={12} />
                        )}
                        {isRetrying ? 'Retrying...' : 'Retry'}
                    </button>
                )}

                {currentJob.status === 'COMPLETED' && activeSubscription && 
                (activeSubscription.tier.name === 'Tier 2' || activeSubscription.tier.name === 'Tier 3') && 
                timeUntilRedetectExpires !== null && timeUntilRedetectExpires > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isVodDeletionTimerActive && timeUntilVodDeletion && timeUntilVodDeletion > 0) {
                                setShowRedetectModal(true);
                            } else if (currentJob.status === 'COMPLETED') {
                                alert(vodDeletionMessage || 'VOD has been deleted or will be deleted soon. Redetection is not possible.');
                            }
                        }}
                        disabled={isRedetecting || (currentJob.redetectionCount !== undefined && currentJob.redetectionCount >= 2) || !isVodDeletionTimerActive}
                        className={`
                            flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200
                            bg-purple-500/20 border border-purple-500/30 text-purple-300
                            hover:bg-purple-500/30 hover:border-purple-500/50
                            disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]
                        `}
                    >
                        {isRedetecting ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <Wand2 size={12} />
                        )}
                        {isRedetecting ? 'Processing...' : `Redetect (${Math.floor(timeUntilRedetectExpires / 60)}:${String(timeUntilRedetectExpires % 60).padStart(2, '0')})`}
                    </button>
                )}

                {canDelete && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteDialog(true);
                        }}
                        className={`
                            flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200
                            bg-white/5 border border-white/10 text-white/60
                            hover:bg-white/10 hover:border-white/20 min-w-[70px]
                        `}
                    >
                        <Trash2 size={12} />
                        Delete
                    </button>
                )}

                {currentJob.status === 'COMPLETED' && (
                    <>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/clips/${currentJob.id}`);
                            }}
                            className={`
                                flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 text-white
                                hover:shadow-lg hover:shadow-black/20 min-w-[80px]
                                ${!customGradientStyle ? `bg-gradient-to-r ${effectiveStageColor}` : ''}
                            `}
                            style={customGradientStyle ? { background: customGradientStyle } : undefined}
                        >
                            <Eye size={12} />
                            View Clips
                        </button>
                    </>
                )}
                    </div>

                    {/* Error Message Display */}
                    {cancelError && (
                        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                            <div className="flex items-center gap-2">
                                <XCircle size={14} className="text-red-400 flex-shrink-0" />
                                <span className="text-red-300 text-xs">{cancelError}</span>
                                <button
                                    onClick={() => setCancelError(null)}
                                    className="ml-auto text-red-400 hover:text-red-300 transition-colors"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 max-w-sm mx-4">
                        <h3 className="text-lg font-semibold text-white mb-3">Delete Job</h3>
                        <p className="text-white/60 mb-6">
                            This will permanently delete everything related to this job including:
                            <br />
                            <span className="text-white/80 font-medium">• All generated clips</span>
                            <br />
                            <span className="text-white/80 font-medium">• Video files and thumbnails</span>
                            <br />
                            <span className="text-white/80 font-medium">• AI detections and subtitles</span>
                            <br />
                            <br />
                            <span className="text-red-300">This action cannot be undone.</span>
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteDialog(false)}
                                className="flex-1 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    onDeleteJob(currentJob.id);
                                    setShowDeleteDialog(false);
                                }}
                                className="flex-1 px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Redetect Highlights Modal */}
            {showRedetectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-white mb-4">Redetect Highlights with New Prompt</h3>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Enter new LLM Prompt:
                            </label>
                            <textarea
                                value={newPrompt}
                                onChange={(e) => {
                                    const promptValue = e.target.value;
                                    setNewPrompt(promptValue);
                                    if (promptValue) {
                                        const validation = validateLlmPromptClientSide(promptValue);
                                        if (!validation.isValid) {
                                            setNewPromptError(validation.reason || 'Prompt contains disallowed phrases.');
                                        } else {
                                            setNewPromptError(null);
                                        }
                                    } else {
                                        setNewPromptError(null);
                                    }
                                }}
                                placeholder="e.g., Detect moments of high excitement and laughter."
                                className={`w-full h-32 p-3 bg-white/5 border rounded-lg text-white placeholder-white/50 resize-none focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all duration-200 ${
                                    newPromptError ? 'border-red-500/50 focus:ring-red-500/30' : 'border-white/10'
                                }`}
                                disabled={isRedetecting}
                                maxLength={500}
                            />
                            <div className="mt-2 flex justify-between items-center">
                                {newPromptError ? (
                                    <p className="text-xs text-red-400">{newPromptError}</p>
                                ) : (
                                    <div />
                                )}
                                <p className="text-xs text-white/40">
                                    {newPrompt.length} / 500
                                </p>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Extra Clips to Generate (1-5): <span className="font-semibold text-white">{additionalClipCount}</span>
                            </label>
                            <input
                                type="range"
                                min="1"
                                max="5"
                                value={additionalClipCount}
                                onChange={(e) => setAdditionalClipCount(parseInt(e.target.value, 10))}
                                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                                disabled={isRedetecting}
                            />
                        </div>

                        <div className="mb-6">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={deletePreviousClips}
                                    onChange={(e) => setDeletePreviousClips(e.target.checked)}
                                    className="h-4 w-4 text-white border-white/20 rounded focus:ring-white/20 bg-white/5"
                                    disabled={isRedetecting}
                                />
                                <label className="ml-2 block text-sm text-white/80">
                                    Delete previous clips?
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowRedetectModal(false);
                                    setNewPrompt('');
                                    setNewPromptError(null);
                                }}
                                className="flex-1 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 transition-all duration-200"
                                disabled={isRedetecting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRedetectSubmit}
                                disabled={isRedetecting || !newPrompt.trim() || !!newPromptError}
                                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-black/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                {isRedetecting ? (
                                    <div className="flex items-center justify-center">
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                        Submitting...
                                    </div>
                                ) : (
                                    'Submit New Prompt'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernJobItem;